import React, { useEffect, useState } from "react";
import { formatCurrency, COMMITTEE } from "../utils/mockData";
import { leaderboardApi } from "../utils/api";

const POLL_INTERVAL_MS = 20000;

function ScrollHint() {
  return (
    <p className="sm:hidden text-center text-[11px] text-gray-400 py-1.5 bg-gray-50 border-b border-gray-100">
      ← Swipe the table to see all columns →
    </p>
  );
}

function SponsorsTable({ sponsors }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[420px] sm:min-w-0">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-100">
            <th className="text-left text-xs font-semibold text-gray-500 px-3 py-3">SL No</th>
            <th className="text-left text-xs font-semibold text-gray-500 px-3 py-3">Name</th>
            <th className="text-left text-xs font-semibold text-gray-500 px-3 py-3">Item</th>
            <th className="text-right text-xs font-semibold text-gray-500 px-3 py-3 sticky right-0 bg-gray-50 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">
              Amount
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {sponsors.map((s, i) => (
            <tr key={i} className="hover:bg-gray-50/50 transition-colors">
              <td className="px-3 py-3 text-sm font-semibold text-gray-500">{i + 1}</td>
              <td className="px-3 py-3 text-sm font-semibold text-gray-800">{s.name}</td>
              <td className="px-3 py-3 text-sm text-gray-600">{s.item || "-"}</td>
              <td className="px-3 py-3 text-right text-sm font-bold text-orange-600 sticky right-0 bg-white shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">
                {Number(s.amount) > 0 ? formatCurrency(s.amount) : "In-Kind"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DonorsTable({ donors }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] sm:min-w-0">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-100">
            <th className="text-left text-xs font-semibold text-gray-500 px-3 py-3">SL No</th>
            <th className="text-left text-xs font-semibold text-gray-500 px-3 py-3">Name</th>
            <th className="text-left text-xs font-semibold text-gray-500 px-3 py-3">S/D/O</th>
            <th className="text-left text-xs font-semibold text-gray-500 px-3 py-3">Address</th>
            <th className="text-right text-xs font-semibold text-gray-500 px-3 py-3 sticky right-0 bg-gray-50 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">
              Donation Amount
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {donors.map((d, i) => (
            <tr key={i} className="hover:bg-gray-50/50 transition-colors">
              <td className="px-3 py-3 text-sm font-semibold text-gray-500">{i + 1}</td>
              <td className="px-3 py-3 text-sm font-semibold text-gray-800">{d.donorName}</td>
              <td className="px-3 py-3 text-sm text-gray-600">{d.soName || "-"}</td>
              <td className="px-3 py-3 text-sm text-gray-600">{d.address || "-"}</td>
              <td className="px-3 py-3 text-right text-sm font-bold text-orange-600 sticky right-0 bg-white shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">
                {formatCurrency(d.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Leaderboard() {
  const [donors, setDonors] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = () => {
      leaderboardApi.list()
        .then((res) => {
          if (cancelled) return;
          const sortedDonors = [...(res.leaderboard || [])].sort((a, b) => Number(b.amount) - Number(a.amount));
          const sortedSponsors = [...(res.sponsors || [])].sort((a, b) => Number(b.amount) - Number(a.amount));
          setDonors(sortedDonors);
          setSponsors(sortedSponsors);
          setError("");
        })
        .catch(() => !cancelled && setError("Couldn't load the latest data. Retrying..."))
        .finally(() => !cancelled && setLoading(false));
    };

    load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const total = donors.reduce((s, d) => s + Number(d.amount || 0), 0);

  return (
    <div className="min-h-screen bg-orange-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="text-center mb-6">
          <img
            src="/logo.png"
            alt="Logo"
            className="w-16 h-16 mx-auto mb-2"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <h1 className="text-xl font-bold text-gray-900">{COMMITTEE.name}</h1>
          <p className="text-sm text-gray-500">Ganesh Chaturthi {COMMITTEE.year} — Donor Wall</p>
        </div>

        {error && (
          <div className="text-center text-xs text-red-500 mb-3">{error}</div>
        )}

        {loading ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Sponsors */}
            <h2 className="text-sm font-bold text-gray-700 mb-2 px-1">Sponsors</h2>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6">
              {sponsors.length === 0 ? (
                <div className="text-center py-10 text-sm text-gray-400">No sponsors recorded yet</div>
              ) : (
                <>
                  <ScrollHint />
                  <SponsorsTable sponsors={sponsors} />
                </>
              )}
            </div>

            {/* Donors */}
            <h2 className="text-sm font-bold text-gray-700 mb-2 px-1">Donations</h2>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              {donors.length === 0 ? (
                <div className="text-center py-16 text-sm text-gray-400">No donations recorded yet</div>
              ) : (
                <>
                  <ScrollHint />
                  <DonorsTable donors={donors} />
                </>
              )}
              <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50">
                <span className="text-sm font-semibold text-gray-600">Total Collection</span>
                <span className="text-sm font-bold text-orange-600">{formatCurrency(total)}</span>
              </div>
            </div>
          </>
        )}

        <p className="text-center text-xs text-gray-400 mt-6">Ganapati Bappa Morya! 🙏 — utsav.btrix.in</p>
      </div>
    </div>
  );
}
